<script setup lang="ts">
import AvatarFull from '@/assets/icons/Avatars/AvatarFull.vue';
import AvatarLite from '@/assets/icons/Avatars/AvatarLite.vue';
import AvatarMedium from '@/assets/icons/Avatars/AvatarMedium.vue';
import { useInAppPurchase } from '@/composables/useInAppPurchase';
import { usePlatform } from '@/composables/usePlatform';
import type { SubscriptionPlan } from '@/types/api';
import 'primeicons/primeicons.css';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
  plans: SubscriptionPlan[];
  currentPlanId: number | null;
  isYearly?: boolean;
}>();

const { t } = useI18n();
const { isNative, isIOS } = usePlatform();
const { isInitialized: isIAPInitialized, getApplePrice } = useInAppPurchase();

// Check if we should use Apple IAP prices
const useApplePricing = computed(() => {
  return isNative.value && isIOS.value && isIAPInitialized.value;
});

const getAvatarComponent = (plan: SubscriptionPlan) => {
  const avatarType = plan.avatar ?? 'lite';
  if (avatarType === 'medium') return AvatarMedium;
  if (avatarType === 'full') return AvatarFull;
  return AvatarLite;
};

// Group and sort plans by category
const plansByCategory = computed(() => {
  const grouped: Record<string, SubscriptionPlan[]> = {};
  for (const plan of props.plans) {
    if (!grouped[plan.category]) grouped[plan.category] = [];
    grouped[plan.category].push(plan);
  }
  Object.keys(grouped).forEach((cat) => {
    grouped[cat].sort((a, b) => a.order - b.order);
  });
  return grouped;
});

const getDisplayPrice = (plan: SubscriptionPlan) => {
  const billingPeriod = props.isYearly ? 'yearly' : 'monthly';

  // On native iOS with IAP initialized, use Apple prices directly
  if (useApplePricing.value) {
    const applePriceInfo = getApplePrice(plan, billingPeriod);
    if (applePriceInfo.price && applePriceInfo.currency) {
      const label = props.isYearly ? t('subscription.yearly') : t('subscription.monthly');
      return `${applePriceInfo.currency} ${applePriceInfo.priceValue} ${label}`;
    }
  }

  const monthlyPrice = Number(plan.price_monthly) || 0;
  const yearlyPrice = Number(plan.price_yearly) || 0;

  if (monthlyPrice === 0 && yearlyPrice === 0) return '-';

  if (props.isYearly && yearlyPrice > 0) return `CHF ${yearlyPrice.toFixed(2)} ${t('subscription.yearly')}`;
  if (!props.isYearly && monthlyPrice > 0) return `CHF ${monthlyPrice.toFixed(2)} ${t('subscription.monthly')}`;
  if (yearlyPrice > 0) return `CHF ${yearlyPrice.toFixed(2)} ${t('subscription.yearly')}`;
  return `CHF ${monthlyPrice.toFixed(2)} ${t('subscription.monthly')}`;
};

// Order and labels based on screenshot and SubscriptionPlanFeatures
const features = [
  { key: 'price', type: 'price' },
  { key: 'ai_generations_per_month', type: 'number' },
  { key: 'employees_included', type: 'number' },
  { key: 'admin_accounts', type: 'number' },
  { key: 'module_orders', type: 'bool' },
  { key: 'module_staff', type: 'bool' },
  // { key: 'custom_branding', type: 'bool' },
  { key: 'social_sharing', type: 'bool' },
];
</script>

<template>
  <div class="flex flex-col gap-8 overflow-x-auto">
    <div v-for="(thePlans, category) in plansByCategory" :key="category">
      <div class="mb-2 font-semibold text-base text-primary-900">{{ t(`subscription.category.${category}`) }}</div>
      <table class="mb-8 min-w-full rounded-lg bg-white">
        <thead>
          <tr class="text-primary-900">
            <th class="px-2 py-3 text-left font-bold"></th>
            <th
              v-for="plan in thePlans"
              :key="plan.id"
              class="plan-col px-2 py-3 text-center font-bold uppercase"
              :class="{ 'bg-primary-100': plan.id === currentPlanId }"
            >
              <div class="flex flex-col items-center">
                <component :is="getAvatarComponent(plan)" class="mb-1 h-14 w-8 sm:h-20 sm:w-12" />
                <span class="text-[10px] sm:text-xs">{{ plan.name }}</span>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="feature in features" :key="feature.key" class="border-t border-gray-200">
            <td class="border-t border-gray-200 px-2 py-2 text-xs text-primary-900 sm:text-sm">
              {{ t(`subscription.features.${feature.key}.label`) }}
            </td>
            <td
              v-for="thePlan in thePlans"
              :key="thePlan.id"
              class="plan-col border-t border-gray-200 px-2 py-2 text-center text-xs sm:text-sm"
              :class="{ 'bg-primary-100': thePlan.id === currentPlanId }"
            >
              <div v-if="feature.type === 'price'">
                <span class="font-bold">{{ getDisplayPrice(thePlan) }}</span>
              </div>
              <div v-else-if="feature.type === 'bool'">
                <i v-if="thePlan.features[feature.key]" class="pi pi-check text-green-600"></i>
                <i v-else class="pi pi-times text-gray-400"></i>
              </div>
              <div v-else-if="feature.type === 'number'">
                <span
                  v-if="
                    (feature.key === 'ai_generations_per_month' && thePlan.features[feature.key] >= 999) ||
                    (feature.key === 'employees_included' && thePlan.features[feature.key] >= 999)
                  "
                  >{{ t('subscription.unlimited') }}</span
                >
                <span v-else>{{ thePlan.features[feature.key] }}</span>
              </div>
              <div v-else-if="feature.type === 'string'">
                <span>{{ thePlan.features[feature.key] }}</span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
table {
  border-collapse: separate;
  border-spacing: 0;
}

th,
td {
  border-right: 1px solid #e5e7eb;
}

th:last-child,
td:last-child {
  border-right: none;
}

/* Make all plan columns the same width */
th.plan-col,
td.plan-col {
  width: 100px;
  min-width: 65px;
  max-width: 100px;
}

@media screen and (max-width: 450px) {
  th.plan-col,
  td.plan-col {
    width: 65px;
    min-width: 65px;
    max-width: 65px;
  }
}
</style>
